import { RefObject, useEffect, useRef, useState } from "react"

/**
 * 检查 DOM 元素的内容是否发生溢出
 * 并使用 ResizeObserver 监听元素尺寸变化以重新检查
 * @returns [isOverflowing, ref]
 * - isOverflowing: boolean 文本是否溢出
 * - ref: React.RefObject<T | null> 需要绑定的 DOM 引用
 */
const useIsOverflow = <T extends HTMLElement = HTMLDivElement>(): [boolean, RefObject<T | null>] => {
  const ref = useRef<T>(null)
  const [isOverflowing, setIsOverflowing] = useState<boolean>(false)

  const checkOverflow = () => {
    const element = ref.current
    if (!element) return
    const child = element.firstElementChild as HTMLElement | null
    const target = child ?? element
    const scrollH = target.scrollHeight
    const clientH = target.clientHeight
    setIsOverflowing(scrollH > clientH + 1)
  }

  useEffect(() => {
    // 文本或组件更新时，重新检查一次
    checkOverflow()

    const element = ref.current
    if (!element) return

    // 实例化 ResizeObserver 并监听元素
    const target = (element.firstElementChild as HTMLElement | null) ?? element
    const observer = new ResizeObserver(checkOverflow)
    observer.observe(target)

    // 清理函数：在组件卸载或 effect 重新运行时停止监听
    return () => {
      observer.unobserve(target)
      observer.disconnect()
    }
  }, []) // 移除 text 依赖，防止传入组件时因引用变化导致频繁重建 Observer

  return [isOverflowing, ref]
}

export default useIsOverflow
